import React, { useEffect, useState } from "react";
import axios from "axios"; 
import { useParams, useNavigate, Link } from "react-router-dom";
import config from '../../config';

function CategoryDetails() {
    const { id } = useParams();
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeLanguage, setActiveLanguage] = useState('en');
    const navigate = useNavigate();

    const fetchCategoryDetails = async () => {
        try {
            const response = await axios.get(`${config.BASE_URL}/api/admin/categories/admin/${id}`, { withCredentials: true });
            setCategory(response.data?.data || null);
        } catch (err) {
            setError('Failed to fetch category details');
            console.error('Error fetching category:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategoryDetails();
    }, [id]);

    const handleDelete = async () => {
        const confirmDelete = window.confirm('Are you sure you want to delete this category? This action cannot be undone.');
        if (!confirmDelete) return;

        try {
            await axios.delete(`${config.BASE_URL}/api/admin/categories/${id}`, { withCredentials: true });
            navigate('/admin/category');
        } catch (err) {
            setError('Failed to delete the category');
            console.error('Error deleting category:', err);
        }
    };

    if (loading) return <div className="dashboard"><div className="container"><p>Loading...</p></div></div>;
    if (error) return <div className="dashboard"><div className="container"><p className="error-message">{error}</p></div></div>;
    if (!category) return <div className="dashboard"><div className="container"><p>Category not found.</p></div></div>;

    return (
        <div className="dashboard project_detail_wrap">
            <div className="container">
                <h1 className="dash_title">Dashboard</h1>
                <div className="back_btn_wrap">
                    <button className="back-btn" onClick={() => navigate(-1)}>Go Back</button>
                </div>

                {/* Language Tabs */}
                <div className="language-tabs" style={{ marginBottom: '20px' }}>
                    <button 
                        type="button" 
                        className={`tab-btn ${activeLanguage === 'en' ? 'active' : ''}`}
                        onClick={() => setActiveLanguage('en')}
                        style={{
                            padding: '10px 20px',
                            marginRight: '10px',
                            backgroundColor: activeLanguage === 'en' ? '#007bff' : '#f8f9fa',
                            color: activeLanguage === 'en' ? 'white' : 'black',
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            borderRadius: '4px'
                        }}
                    >
                        English
                    </button>
                    <button 
                        type="button" 
                        className={`tab-btn ${activeLanguage === 'es' ? 'active' : ''}`}
                        onClick={() => setActiveLanguage('es')}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: activeLanguage === 'es' ? '#007bff' : '#f8f9fa',
                            color: activeLanguage === 'es' ? 'white' : 'black',
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            borderRadius: '4px'
                        }}
                    >
                        Spanish
                    </button>
                </div>

                <div className="main-section">
                    <div className="projects">
                        <div className="projects-inner">
                            {/* English Content */}
                            {activeLanguage === 'en' && (
                                <div className="language-content">
                                    <h3 style={{ marginBottom: '20px', color: '#007bff' }}>English Content</h3>

                                    <div className="detail_item">
                                        <h2>Language</h2>
                                        <div className="detail_item_inner">
                                            <p>{category.language || 'en'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Name</h2>
                                        <div className="detail_item_inner">
                                            <p>{category?.name || 'No name provided'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Description</h2>
                                        <div className="detail_item_inner">
                                            <p>{category?.description || 'No description provided'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Spanish Content */}
                            {activeLanguage === 'es' && (
                                <div className="language-content">
                                    <h3 style={{ marginBottom: '20px', color: '#007bff' }}>Spanish Content</h3>

                                    <div className="detail_item">
                                        <h2>Language</h2>
                                        <div className="detail_item_inner">
                                            <p>{category.spanishData?.language || 'es'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Name (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{category.spanishData?.name || 'No se proporcionó nombre'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Description (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{category.spanishData?.description || 'No se proporcionó descripción'}</p>
                                        </div>
                                    </div>

                                    {!category.spanishData?.name && !category.spanishData?.description && (
                                        <div style={{ 
                                            padding: '15px', 
                                            backgroundColor: '#f8f9fa', 
                                            border: '1px solid #dee2e6', 
                                            borderRadius: '4px',
                                            marginTop: '10px'
                                        }}>
                                            <p style={{ margin: 0, color: '#6c757d', fontStyle: 'italic' }}>
                                                No Spanish translation available for this category.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Category Metadata */}
                            <div className="detail_item">
                                <h2>Category Information</h2>
                                <div className="detail_item_inner">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '10px' }}>
                                        <div>
                                            <strong>Created:</strong> 
                                            <p style={{ margin: '5px 0' }}>
                                                {category.createdAt ? new Date(category.createdAt).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <strong>Last Updated:</strong>
                                            <p style={{ margin: '5px 0' }}>
                                                {category.updatedAt ? new Date(category.updatedAt).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="detail_project_btn_wrap">
                                <div className="detail_edit_del">
                                    <div className="btn_item">
                                        <button 
                                            className="detail_btns" 
                                            onClick={handleDelete}
                                            style={{
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                padding: '10px 20px',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                    <div className="btn_item">
                                        <Link 
                                            to={`/admin/category-update/${category?._id}`} 
                                            className="update_detail detail_btns"
                                            style={{
                                                backgroundColor: '#28a745',
                                                color: 'white',
                                                textDecoration: 'none',
                                                padding: '10px 20px',
                                                borderRadius: '4px',
                                                display: 'inline-block'
                                            }}
                                        >
                                            Edit
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CategoryDetails;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { useParams, useNavigate, Link } from "react-router-dom";
// import config from '../../config';

// function CategoryDetails() {
//     const { id } = useParams();
//     const [category, setCategory] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState('');
//     const navigate = useNavigate();

//     const fetchCategoryDetails = async () => {
//         try {
//             const response = await axios.get(`${config.BASE_URL}/api/admin/categories/${id}`, { withCredentials: true });
//             setCategory(response.data.data);
//         } catch (err) {
//             console.error('Error fetching category details:', err);
//             if (err.response?.status === 404) {
//                 setError('Category not found');
//             } else if (err.response?.status === 401) {
//                 setError('Unauthorized. Please login again.');
//                 navigate('/admin/login');
//                 return;
//             } else {
//                 setError('Failed to fetch category details');
//             }
//         } finally {
//             setLoading(false);
//         }
//     }

//     useEffect(() => {
//         fetchCategoryDetails();
//     }, [id]);

//     const handleDelete = async () => {
//         if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
//             return;
//         }

//         try {
//             await axios.delete(`${config.BASE_URL}/api/admin/categories/${id}`, { withCredentials: true });
//             navigate('/admin/category');
//         } catch (err) {
//             if (err.response?.status === 401) {
//                 setError('Unauthorized. Please login again.');
//                 navigate('/admin/login');
//             } else if (err.response?.status === 404) {
//                 setError('Category not found');
//             } else {
//                 setError('Failed to delete the category');
//             }
//         }
//     };

//     const formatDate = (dateString) => {
//         if (!dateString) return 'N/A';
//         return new Date(dateString).toLocaleDateString('en-US', {
//             year: 'numeric',
//             month: 'long',
//             day: 'numeric',
//             hour: '2-digit',
//             minute: '2-digit'
//         });
//     };

//     if (loading) return (
//         <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//             <div className="text-center">
//                 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
//                 <p className="text-lg text-gray-600">Loading category details...</p>
//             </div>
//         </div>
//     );

//     if (error) return (
//         <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//             <div className="text-center">
//                 <div className="bg-white p-8 rounded-lg shadow-sm border border-red-200">
//                     <p className="text-lg text-red-600 mb-4">{error}</p>
//                     <button
//                         onClick={() => navigate(-1)}
//                         className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-black transition-colors"
//                     >
//                         Go Back
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );

//     if (!category) return (
//         <div className="min-h-screen bg-gray-50 flex items-center justify-center">
//             <div className="text-center">
//                 <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
//                     <p className="text-lg text-gray-600 mb-4">No category found</p>
//                     <button
//                         onClick={() => navigate(-1)}
//                         className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-black transition-colors"
//                     >
//                         Go Back
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );

//     return (
       
//         <div className="dashboard">
//             <div className="container">
//                 <div className="main-section project_type_details">
//                    <div className="topsection d-flex align-items-center justify-content-between mb-8">
//                         <h1 className="text-white">Category Details</h1>
//                         <button
//                             onClick={() => navigate(-1)}
//                             className="bg-white text-black"
//                         >
//                             Go Back
//                         </button>
//                     </div>

//                     <div className="main-inner add_project_main">
//                         <div className="project_main_inr">
//                             <div className="project_block">
//                                 <div className="project_main_block">
//                                     <div className="d-flex align-items-center justify-content-between">
//                                         <h2 className="text-white">Category Name </h2>
//                                         <p className="text-white">{category.name}</p>
//                                     </div>
//                                     <div className="d-flex align-items-center justify-content-between">
//                                         <h2 className="text-white">Description</h2>
//                                         <div className="text-gray-700">
//                                             {category.description ? (
//                                                 <p className="whitespace-pre-wrap">{category.description}</p>
//                                             ) : (
//                                                 <p className="italic text-gray-500">No description provided</p>
//                                             )}
//                                         </div>
//                                     </div>
                                  
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                     <div className="add_prprty_btn d-flex align-items-center gap-10 ptb-30">
//                         <button
//                             onClick={handleDelete}
//                             className="bg_red text-white"
//                         >
//                             Delete Category
//                         </button>
//                         <Link
//                             to={`/admin/category-update/${category._id}`}
//                             className="edit_btn"
//                         >
//                             Edit Category
//                         </Link>
//                     </div>
//                     {error && (
//                         <div className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
//                             {error}
//                         </div>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// }

// export default CategoryDetails;